// Supply-drop shop (the intermission STORE): score is the spendable wallet —
// points spent on supplies now vs banked for the final score is the intended
// tension. XP (motes → Lumensurge) stays the separate in-run power curve.
// Pure deterministic core: stock and boon draws come from private mulberry32
// streams derived from the run seed + wave (RngStream, UpgradeSystem-salt
// pattern); the shared world streams are never touched, so sim determinism
// is unaffected. DOM/render code lives in ui/shop-overlay.ts.

import type { Player } from '../types.js';
import { RngStream } from './rng.js';
import { isUpgradeOfferEligible, type UpgradeDef, type UpgradeRarity } from './upgrade-system.js';
import upgradesData from '../content/upgrades.json';

export type ShopItemId = 'repair' | 'hull' | 'rare_boon' | 'mythical_boon' | 'flash_cell' | 'vitals_pod';

export interface ShopItemDef {
  id: ShopItemId;
  name: string;
  /** One-line effect summary shown on the card. */
  effect: string;
  /** Price in score points (the wallet total, not motes). */
  cost: number;
  /** HackerNoon pixel-icon glyph name. */
  icon: string;
}

/** HP restored by the repair kit (clamped at max_hp). */
export const REPAIR_AMOUNT = 25;
/** Max-hull (and hull) granted by the plating kit. */
export const HULL_AMOUNT = 20;
/** Max-HP (and heal) granted by the vitals pod. */
export const VITALS_AMOUNT = 20;
/** Flash charges granted (and immediately recharged) by the flash cell. */
export const FLASH_CELL_CHARGES = 1;

/** The store's whole catalogue. Stock is a 4-item subset per wave. Prices are
 * BASE costs — the live price scales with the wave (see priceFor) so
 * late-game score inflation can't trivialize the shelf.
 *
 * Price grounding (score economy): a wave pays kills × 100 + pickups × 25 +
 * 10/s survived + 25 × wave on clear. The wave budget (95 + 38 × wave threat,
 * ≈4 threat per early enemy) works out to ≈33 kills on wave 1 — roughly
 * 4,000–4,500 score for an early wave, growing ≈1,200/wave. Base costs peg
 * to that: repair ≈ 1 wave, hull ≈ 2, rare boon ≈ 4, mythical ≈ 7. */
export const SHOP_ITEMS: readonly ShopItemDef[] = [
  { id: 'repair', name: 'FIELD REPAIR', effect: `RESTORE ${REPAIR_AMOUNT} HP`, cost: 4000, icon: 'heart-solid' },
  { id: 'hull', name: 'HULL PLATING', effect: `+${HULL_AMOUNT} HULL`, cost: 8000, icon: 'octagon-check-solid' },
  { id: 'rare_boon', name: 'RARE BOON', effect: 'Random rare upgrade', cost: 16000, icon: 'star' },
  { id: 'mythical_boon', name: 'MYTHICAL BOON', effect: 'Random mythical upgrade', cost: 28000, icon: 'sparkles' },
  { id: 'flash_cell', name: 'FLASH CELL', effect: `+${FLASH_CELL_CHARGES} max flash charge (and recharge it)`, cost: 6000, icon: 'bolt' },
  { id: 'vitals_pod', name: 'VITALS POD', effect: `+${VITALS_AMOUNT} max HP and heal ${VITALS_AMOUNT}`, cost: 3500, icon: 'heart-solid' }
];

/** Inflation: +25% per wave after the first, so prices track the roughly
 * linear growth of per-wave score income (≈ +1,200/wave, see above). */
const PRICE_WAVE_MULT = 0.25;

/** Live price of an item at a wave: base × (1 + 0.25 × (wave − 1)). */
export function priceFor(item: ShopItemDef, wave: number): number {
  return Math.round(item.cost * (1 + PRICE_WAVE_MULT * Math.max(0, wave - 1)));
}

const UPGRADE_DEFS = (upgradesData as { version: string; entries: UpgradeDef[] }).entries;

// Private stream salts: shop stock and boon draws stay independent of the
// shared world streams and of each other (UpgradeSystem-salt pattern).
const STOCK_STREAM_SALT = 0x51ED270B;
const BOON_STREAM_SALT = 0xF1EA5EED;
// Wave index folded into the stream seed (golden-ratio mixer).
const WAVE_MIX = 0x9E3779B9;
// Per-rarity offset so the rare and mythical draws of the same wave differ.
const RARITY_MIX: Record<string, number> = { rare: 0x27D4EB2F, mythical: 0x165667B1 };

function mixSeed(seed: number, salt: number, wave: number): number {
  return (((seed ^ salt) >>> 0) ^ Math.imul((wave >>> 0) + 1, WAVE_MIX)) >>> 0;
}

/** Catalogue lookup; throws on an unknown id. */
export function shopItem(id: ShopItemId): ShopItemDef {
  const def = SHOP_ITEMS.find((d) => d.id === id);
  if (!def) throw new Error(`Unknown shop item: ${id}`);
  return def;
}

/** Boon items map to an upgrade rarity; instant items return null. */
export function boonRarityFor(id: ShopItemId): UpgradeRarity | null {
  if (id === 'rare_boon') return 'rare';
  if (id === 'mythical_boon') return 'mythical';
  return null;
}

/**
 * The wave's 4-item stock, deterministic per (seed, wave). 'repair' is always
 * on the shelf; the other three are drawn weighted without replacement from
 * hull 25% / rare boon 25% / flash cell 20% / mythical boon 15% / vitals pod
 * 15% (each later draw renormalizes over what remains).
 */
export function rollShopStock(seed: number, wave: number): ShopItemId[] {
  const stream = new RngStream(mixSeed(seed, STOCK_STREAM_SALT, wave));
  const stock: ShopItemId[] = ['repair'];
  const pool: { id: ShopItemId; weight: number }[] = [
    { id: 'hull', weight: 25 },
    { id: 'rare_boon', weight: 25 },
    { id: 'mythical_boon', weight: 15 },
    { id: 'flash_cell', weight: 20 },
    { id: 'vitals_pod', weight: 15 }
  ];
  while (stock.length < 4 && pool.length > 0) {
    let total = 0;
    for (const entry of pool) total += entry.weight;
    let roll = stream.next() * total;
    let idx = 0;
    for (; idx < pool.length; idx++) {
      roll -= pool[idx].weight;
      if (roll < 0) break;
    }
    idx = Math.min(idx, pool.length - 1);
    stock.push(pool.splice(idx, 1)[0].id);
  }
  return stock;
}

/** Wallet gate: the purchase needs the full price in the score wallet. */
export function canAfford(score: number, cost: number): boolean {
  return score >= cost;
}

/** Resolve stock ids to their catalogue defs, in stock order. */
export function describeStock(
  stock: readonly ShopItemId[],
  defs: readonly ShopItemDef[] = SHOP_ITEMS
): ShopItemDef[] {
  return stock.map((id) => {
    const def = defs.find((d) => d.id === id);
    if (!def) throw new Error(`Unknown shop item: ${id}`);
    return def;
  });
}

/**
 * Score wallet: earned − spent. Purchases are gated through spendWallet, so
 * the total never drops below 0; walletTotal clamps defensively regardless.
 */
export interface ShopWallet {
  earned: number;
  spent: number;
}

export function walletTotal(wallet: ShopWallet): number {
  return Math.max(0, wallet.earned - wallet.spent);
}

/** Charge `cost` against the wallet. Returns false (no mutation) when short. */
export function spendWallet(wallet: ShopWallet, cost: number): boolean {
  if (walletTotal(wallet) < cost) return false;
  wallet.spent += cost;
  return true;
}

/**
 * Apply an instant-effect item to the player. Boons are not instant: roll the
 * def with rollShopBoon and apply it via applyUpgradeToPlayer (upgrade-system).
 */
export function applyShopItem(player: Player, id: ShopItemId): void {
  switch (id) {
    case 'repair':
      player.hp = Math.min(player.max_hp, player.hp + REPAIR_AMOUNT);
      break;
    case 'hull':
      player.max_hull += HULL_AMOUNT;
      player.hull += HULL_AMOUNT;
      break;
    case 'flash_cell':
      // Same wiring as the Twin Flash boon (dash_charges): the bonus raises
      // the pool cap in movement.ts, and the new charge is usable at once.
      player.dashChargeBonus = (player.dashChargeBonus ?? 0) + FLASH_CELL_CHARGES;
      player.dash.charges += FLASH_CELL_CHARGES;
      break;
    case 'vitals_pod':
      player.max_hp += VITALS_AMOUNT;
      player.hp = Math.min(player.max_hp, player.hp + VITALS_AMOUNT);
      break;
    default:
      break;
  }
}

/**
 * Draw one upgrade of `rarity` the player is currently eligible for —
 * same predicate as the Lumensurge/boon offers (max rank via `rankOf`, slot
 * capacity, weapon-grant ownership). Deterministic per (seed, wave, rarity):
 * re-rolling costs nothing and always returns the same def. Returns null when
 * nothing of that rarity is eligible — the shop shows SOLD OUT and no score
 * changes hands (the refund path never triggers).
 */
export function rollShopBoon(
  seed: number,
  wave: number,
  rarity: UpgradeRarity,
  player: Player,
  rankOf: (id: string) => number
): UpgradeDef | null {
  const eligible = UPGRADE_DEFS.filter(
    (d) => d.rarity === rarity && isUpgradeOfferEligible(player, d, rankOf(d.id))
  );
  if (eligible.length === 0) return null;
  const salt = (BOON_STREAM_SALT ^ (RARITY_MIX[rarity] ?? 0)) >>> 0;
  const stream = new RngStream(mixSeed(seed, salt, wave));
  return eligible[stream.next_int(eligible.length)];
}
